import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import type { FC, ReactNode } from 'react';

interface PostEditorSurfaceProps {
  title: string;
  onTitleChange: (value: string) => void;
  titleError?: string;
  titlePlaceholder?: string;
  /** Rendered to the right of the title input (e.g. the composer's close button). */
  titleAction?: ReactNode;
  /** Body editor height bounds — the phone sheets pass taller values so the
      body doesn't feel cramped in a full-height composer. */
  editorMinHeight?: string;
  editorMaxHeight?: string;
  /** The RichTextEditor. Its own frame is stripped — the surface provides it. */
  children: ReactNode;
}

/**
 * Unified input surface shared by the post composer and the edit form: a
 * borderless title input on top of the rich-text editor, framed as a single
 * field with a focus ring — instead of two stacked boxed inputs.
 */
export const PostEditorSurface: FC<PostEditorSurfaceProps> = ({
  title,
  onTitleChange,
  titleError,
  titlePlaceholder = 'Add a title (optional)',
  titleAction,
  editorMinHeight = '110px',
  editorMaxHeight = '260px',
  children,
}) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: titleError ? 'error.main' : 'divider',
      borderRadius: '12px',
      overflow: 'hidden',
      bgcolor: 'common.white',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      '&:hover': { borderColor: titleError ? 'error.main' : 'grey.400' },
      '&:focus-within': {
        borderColor: titleError ? 'error.main' : 'primary.main',
        boxShadow: theme => `0 0 0 3px ${theme.palette.primary.main}14`,
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '15px', pt: '10px', pb: '8px' }}>
      <InputBase
        value={title}
        onChange={e => onTitleChange(e.target.value)}
        fullWidth
        placeholder={titlePlaceholder}
        sx={{
          fontFamily: 'Figtree, sans-serif',
          fontSize: 17,
          fontWeight: 700,
          color: '#1a1a1a',
          p: 0,
          '& .MuiInputBase-input': { p: 0 },
          // The typed title stays 17px; only the placeholder hint is shrunk to
          // the body editor's size (body2 = 0.875rem), kept bold.
          '& .MuiInputBase-input::placeholder': { color: '#b2b2b2', opacity: 1, fontWeight: 600, fontSize: '0.875rem' },
        }}
      />
      {titleAction}
    </Box>
    {titleError && (
      <Typography sx={{ px: '15px', pb: '6px', fontSize: 12, color: 'error.main' }}>{titleError}</Typography>
    )}
    {/* The editor draws its own border for standalone use (comments); inside
        this surface it's redundant, so strip it and keep only a top divider
        separating the title from the formatting toolbar. A post body deserves
        more room than the comment-sized default, and the placeholder should
        match the title's, so both are re-tuned here rather than globally. */}
    <Box
      sx={{
        borderTop: '1px solid',
        borderTopColor: 'divider',
        '& > div': { border: 'none', borderRadius: 0 },
        '& .ProseMirror': { minHeight: editorMinHeight, maxHeight: editorMaxHeight },
        '& .ProseMirror p.is-editor-empty:first-of-type::before': { color: '#b2b2b2' },
      }}
    >
      {children}
    </Box>
  </Box>
);
